import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Wand2, Settings2, Send, AlertTriangle, FlaskConical } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PlannerCalendar from "@/components/PlannerCalendar";
import PlannerClientTable from "@/components/PlannerClientTable";
import PlannerSettingsDialog from "@/components/PlannerSettingsDialog";
import InternalBlocksCard from "@/components/InternalBlocksCard";
import PlannerReviewPanel from "@/components/PlannerReviewPanel";
import PlannerOverview from "@/components/PlannerOverview";
import AppointmentImportPanel from "@/components/AppointmentImportPanel";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

function Kpi({ label, value, tone = "", testId }) {
  return (
    <div className={`border border-slate-200 bg-white px-4 py-3 ${tone}`} data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">{label}</div>
      <div className="font-heading text-2xl font-light text-slate-900 mt-1">{value}</div>
    </div>
  );
}

export default function TerminplanungPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const isMgmt = (user?.all_roles || [user?.role]).some((r) => r === "admin" || r === "manager");
  const [coachId, setCoachId] = useState("");
  const [dash, setDash] = useState(null);
  const [plan, setPlan] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(false);
  const [outbox, setOutbox] = useState([]);
  const [picked, setPicked] = useState([]);
  const [rk, setRk] = useState(0);
  const q = coachId ? { coach_id: coachId } : {};
  const load = () => {
    api.get("/planner/dashboard", { params: q }).then((r) => setDash(r.data)).catch(err);
    api.get("/planner/drafts", { params: q }).then((r) => setPlan(r.data)).catch(err);
    api.get("/planner/outbox").then((r) => setOutbox(r.data)).catch(() => {});
    setRk((x) => x + 1);
  };
  useEffect(() => { load(); }, [coachId]); // eslint-disable-line
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/planner/run", { coach_id: coachId || undefined, client_ids: picked.length ? picked : undefined });
      setWarnings(data.warnings);
      toast.success(`${data.drafts.length} ${data.drafts.length === 1 ? "Terminvorschlag" : "Terminvorschläge"} erstellt – bitte prüfen und freigeben`);
      load();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  useEffect(() => { if (params.get("auto") === "1") run(); }, []); // eslint-disable-line
  const send = async () => {
    if (!window.confirm("Alle freigegebenen Vorschläge jetzt an die Teilnehmer senden?")) return;
    try {
      const { data } = await api.post("/planner/send", { coach_id: coachId || undefined });
      toast.success(`${data.sent_clients} Teilnehmer ${data.test_mode ? "(Testmodus – nur Vorschau)" : "per E-Mail"} benachrichtigt${data.skipped.length ? ` · ${data.skipped.length} ohne E-Mail` : ""}`);
      load();
    } catch (e) { err(e); }
  };
  if (!dash || !plan) return <div className="px-8 py-10 text-xs text-slate-400">Lädt…</div>;
  const k = dash.kpis;
  const approvedCount = plan.drafts.filter((d) => d.status === "approved").length;
  const open = dash.clients;
  const togglePick = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1400px] mx-auto" data-testid="terminplanung-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8 border-b border-slate-200 pb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Kalender · Intelligente AVGS-Terminplanung</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">Terminplanung</h1>
          {dash.test_mode && <div className="mt-3 inline-flex items-center gap-1.5 text-xs bg-violet-50 border border-violet-200 text-violet-800 px-2.5 py-1" data-testid="planner-test-mode-badge"><FlaskConical size={12} /> Testmodus aktiv – keine echten E-Mails, keine Google-Kalender-Einträge</div>}
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {isMgmt && dash.team.length > 0 && (
            <select value={coachId} onChange={(e) => setCoachId(e.target.value)} className="input-tech" style={{ width: "14rem" }} data-testid="planner-coach-select">
              <option value="">{user?.name} (ich)</option>
              {dash.team.filter((t) => t.user_id !== user?.user_id).map((t) => <option key={t.user_id} value={t.user_id}>{t.name}</option>)}
            </select>
          )}
          <button onClick={() => setSettings(true)} className="h-10 px-3 border border-slate-300 text-sm hover:bg-slate-100 flex items-center gap-1.5" data-testid="planner-settings-btn"><Settings2 size={14} /> Einstellungen</button>
          <button onClick={run} disabled={busy} className="h-10 px-4 bg-slate-900 text-white text-sm hover:bg-black disabled:opacity-50 flex items-center gap-1.5" data-testid="planner-run-btn"><Wand2 size={14} /> {busy ? "Plant…" : picked.length ? `${picked.length} ausgewählte planen` : "AVGS-Termine automatisch planen"}</button>
          <button onClick={send} disabled={!approvedCount} className="h-10 px-4 border border-[#0a788c] text-[#0a788c] text-sm hover:bg-[#0a788c]/5 disabled:opacity-40 flex items-center gap-1.5" data-testid="planner-send-btn"><Send size={14} /> Freigegebene senden ({approvedCount})</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
        <Kpi label="Aktive Teilnehmer" value={k.active_clients} testId="kpi-active" />
        <Kpi label="Offene Termine" value={k.open_sessions} testId="kpi-open" />
        <Kpi label="Geplant" value={k.planned} testId="kpi-planned" />
        <Kpi label="Bestätigung ausst." value={k.pending_confirmation} testId="kpi-pending" />
        <Kpi label="Bestätigt" value={k.confirmed} testId="kpi-confirmed" />
        <Kpi label="Freie Std. (4 Wo.)" value={k.free_hours} testId="kpi-free" />
        <Kpi label="Auslastung" value={`${k.utilization}%`} testId="kpi-util" />
        <Kpi label="Konflikte" value={k.conflicts} tone={k.conflicts ? "bg-rose-50" : ""} testId="kpi-conflicts" />
      </div>
      {warnings.some((w) => w.level === "info") && (
        <div className="border border-slate-200 bg-slate-50 px-5 py-3 mb-4 text-sm text-slate-700" data-testid="planner-infos">
          {warnings.filter((w) => w.level === "info").map((w, i) => <div key={w.client_id + i}><b>{w.name}</b>: {w.reason}</div>)}
        </div>
      )}
      {warnings.some((w) => w.level !== "info") && (
        <div className="border border-amber-300 bg-amber-50 px-5 py-4 mb-6" data-testid="planner-warnings">
          <div className="text-xs font-bold uppercase tracking-overline text-amber-800 mb-2 flex items-center gap-1.5"><AlertTriangle size={13} /> Nicht vollständig planbar</div>
          {warnings.filter((w) => w.level !== "info").map((w, i) => <div key={w.client_id + i} className="text-sm text-amber-900"><b>{w.name}</b>: {w.reason}</div>)}
        </div>
      )}
      <div className="mb-6">
        <AppointmentImportPanel coaches={dash.team || []} />
      </div>
      <PlannerOverview coachId={coachId} refreshKey={rk} onChange={load} />
      <PlannerClientTable clients={open} picked={picked} onPick={togglePick} onReload={load} ownerName={coachId ? dash.team.find((t) => t.user_id === coachId)?.name : null} />
      <PlannerCalendar coachId={coachId} clients={open} refreshKey={rk} onChange={load} />
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 items-start">
          <PlannerReviewPanel refreshKey={rk} />
          <InternalBlocksCard coachId={coachId} onChange={load} />
          {dash.team.length > 0 && (
            <div className="border border-slate-200 bg-white" data-testid="planner-team">
              <div className="px-4 py-3 border-b border-slate-100 text-[10px] font-bold uppercase tracking-overline text-slate-500">Auslastung je Coach (4 Wochen)</div>
              {dash.team.map((t) => <div key={t.user_id} className="px-4 py-2 text-xs flex justify-between border-b border-slate-50"><span>{t.name}</span><span className="font-mono">{t.upcoming} Termine · {t.planned} Vorschläge</span></div>)}
            </div>
          )}
          {dash.test_mode && outbox.length > 0 && (
            <div className="border border-violet-200 bg-white" data-testid="planner-outbox">
              <div className="px-4 py-3 border-b border-violet-100 text-[10px] font-bold uppercase tracking-overline text-violet-700">Testmodus – nicht versendete E-Mails</div>
              {outbox.slice(0, 8).map((m) => (
                <div key={m.mail_id} className="px-4 py-2 text-xs border-b border-slate-50">
                  <div className="font-medium">{m.subject}</div><div className="text-slate-500">an {m.to}</div>
                  {m.link && <a href={m.link} target="_blank" rel="noreferrer" className="text-[#0a788c] underline" data-testid={`planner-outbox-link-${m.mail_id}`}>Teilnehmer-Ansicht öffnen</a>}
                </div>
              ))}
            </div>
          )}
      </div>
      <PlannerSettingsDialog open={settings} onOpenChange={setSettings} coachId={coachId} onSaved={load} />
    </div>
  );
}
